import type { Ref } from 'vue'
import { ref, computed, watch } from 'vue'

import type { Maybe } from '@/types'

import type { PaginationProvider } from './usePagination.types'

export default function usePagination(l = 25, p = 1) {
  const page = ref(p)
  const limit = ref(l)
  const provider = ref<PaginationProvider | null>(null)
  const pages = computed(() => {
    if (provider.value === null) {
      return 0
    }
    return provider.value.pageCount
  })

  const pagination = computed(() => {
    return {
      hasPrevPage: page.value !== 1,
      hasNextPage: pages.value !== page.value,
    }
  })

  function prev() {
    page.value = Math.max(page.value - 1, p)
  }

  function next() {
    page.value += 1
  }

  function reset() {
    page.value = p
  }

  watch(limit, reset)

  function registerPaginationProvider(value: Ref<Maybe<PaginationProvider>>) {
    watch(
      value,
      (v) => {
        if (v !== undefined) {
          provider.value = v
          return
        }
        provider.value = null
      },
      { immediate: true }
    )
  }

  return {
    pagination,
    page,
    limit,
    prev,
    next,
    reset,
    registerPaginationProvider,
  }
}
